/**
 * One titled section of the Monitor page and the states of its read: loading, failed (with
 * another try), empty, or its rows. The inbox and the watched items differ only in their rows.
 */
import { type LucideIcon, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/** A section's read as the page has it. */
export type LoadView<T> =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; rows: readonly T[] }

export interface MonitorSectionProps<T> {
  heading: { id: string; icon: LucideIcon; title: string }
  /** At the end of the heading's row: the section's own action (*Add rule*). */
  action?: ReactNode
  view: LoadView<T>
  /** What the section says while loading, when the read failed and when there is nothing. */
  copy: { loading: string; failed: string; empty: string }
  onRetry: () => void
  children: (rows: readonly T[]) => ReactNode
}

function Body<T>({
  view,
  copy,
  onRetry,
  children,
}: Omit<MonitorSectionProps<T>, 'heading' | 'action'>) {
  const { t } = useTranslation()
  if (view.status === 'loading') {
    return (
      <div role="status" className="flex flex-col gap-2">
        <span className="sr-only">{copy.loading}</span>
        <Skeleton aria-hidden className="h-12 w-full" />
        <Skeleton aria-hidden className="h-12 w-full" />
      </div>
    )
  }
  if (view.status === 'failed') {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-destructive">{copy.failed}</p>
        <Button type="button" variant="outline" size="xs" onClick={onRetry}>
          <RefreshCw aria-hidden />
          {t('cases.monitor.retry')}
        </Button>
      </div>
    )
  }
  if (view.rows.length === 0) return <p className="text-sm text-muted-foreground">{copy.empty}</p>
  return children(view.rows)
}

export function MonitorSection<T>({ heading, action, ...body }: MonitorSectionProps<T>) {
  const Icon = heading.icon
  return (
    <section aria-labelledby={heading.id} className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          id={heading.id}
          className="flex items-center gap-2 text-sm font-semibold text-foreground"
        >
          <Icon aria-hidden className="size-4 text-muted-foreground" />
          {heading.title}
        </h2>
        {action}
      </div>
      <Body {...body} />
    </section>
  )
}
